import { useMemo, useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  TextField,
  Chip,
} from '@mui/material'
import { Add, Delete as DeleteIcon, Gavel } from '@mui/icons-material'
import { useGetJudgeAssignmentsQuery, useCreateJudgeAssignmentMutation, useDeleteJudgeAssignmentMutation } from '../store/api/judgeAssignmentsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetUsersQuery } from '../store/api/usersApi'
import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { PageHeader } from '../components/PageHeader'
import { getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'

export default function JudgeAssignmentsPage() {
  const { user } = useAppSelector((s) => s.auth)
  const canManage =
    user &&
    (hasPermission(user.role, 'ASSIGN_CASES') || hasPermission(user.role, 'MANAGE_USERS'))

  const { data: rows = [], isLoading } = useGetJudgeAssignmentsQuery(undefined, {
    skip: !canManage && user?.role !== 'judge',
  })
  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: judges = [] } = useGetUsersQuery(
    { role: 'judge' },
    { skip: !canManage }
  )

  const [judgeId, setJudgeId] = useState('')
  const [caseId, setCaseId] = useState('')
  const [teamA, setTeamA] = useState('')
  const [teamB, setTeamB] = useState('')
  const [notes, setNotes] = useState('')
  const [createAssignment, { isLoading: creating }] = useCreateJudgeAssignmentMutation()
  const [deleteAssignment] = useDeleteJudgeAssignmentMutation()

  const caseTeams = useMemo(() => {
    const c = cases.find((x) => x.id === caseId)
    if (!c?.assigned_team_ids?.length) return []
    return teams.filter((t) => c.assigned_team_ids!.includes(t.id))
  }, [caseId, cases, teams])

  const handleCreate = async () => {
    if (!judgeId || !caseId || !teamA) return
    const team_ids = [teamA, teamB].filter(Boolean)
    try {
      await createAssignment({
        judge_id: judgeId,
        case_id: caseId,
        team_ids,
        notes: notes || undefined,
      }).unwrap()
      setNotes('')
      setTeamB('')
    } catch {
      /* RTK surface */
    }
  }

  if (!user) return null

  if (!canManage && user.role !== 'judge') {
    return (
      <Container sx={{ mt: { xs: 2, sm: 3 } }}>
        <Alert severity="error">Bạn không có quyền truy cập trang này.</Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Phân công giám khảo"
        subtitle="Giám khảo chỉ xem vụ án / chứng cứ / memorial / chấm điểm trong phạm vi cặp đội được gán (BR-08-02)."
        icon={<Gavel />}
      />

      {canManage && (
        <Paper elevation={0} sx={(theme) => getFilterPaperSx(theme)}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Thêm phân công
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Giám khảo</InputLabel>
              <Select
                label="Giám khảo"
                value={judgeId}
                onChange={(e) => setJudgeId(e.target.value)}
              >
                {judges.map((j) => (
                  <MenuItem key={j.id} value={j.id}>
                    {j.full_name || j.username} ({j.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Cuộc thi</InputLabel>
              <Select label="Cuộc thi" value={caseId} onChange={(e) => setCaseId(e.target.value)}>
                {cases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Đội 1</InputLabel>
              <Select label="Đội 1" value={teamA} onChange={(e) => setTeamA(e.target.value)}>
                {caseTeams.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Đội 2 (tuỳ chọn)</InputLabel>
              <Select label="Đội 2 (tuỳ chọn)" value={teamB} onChange={(e) => setTeamB(e.target.value)}>
                <MenuItem value="">
                  <em>Không chọn</em>
                </MenuItem>
                {caseTeams
                  .filter((t) => t.id !== teamA)
                  .map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Ghi chú"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              disabled={creating || !judgeId || !caseId || !teamA}
              onClick={handleCreate}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              Lưu phân công
            </Button>
          </Box>
        </Paper>
      )}

      <TableContainer component={Paper} elevation={0} sx={(theme) => getTablePaperSx(theme)}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cuộc thi</TableCell>
              <TableCell>Giám khảo</TableCell>
              <TableCell>Đội</TableCell>
              <TableCell>Ghi chú</TableCell>
              {canManage && <TableCell align="right">Thao tác</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={canManage ? 5 : 4}>Đang tải…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 5 : 4}>
                  <Typography color="text.secondary">Chưa có phân công.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const jc = cases.find((c) => c.id === r.case_id)
                const ju = judges.find((j) => j.id === r.judge_id)
                return (
                  <TableRow key={r.id}>
                    <TableCell>{jc ? formatCaseDisplay(jc) : r.case_id}</TableCell>
                    <TableCell>{ju?.full_name || ju?.username || r.judge_id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {r.team_ids.map((tid) => {
                          const t = teams.find((x) => x.id === tid)
                          return <Chip key={tid} size="small" label={t?.name || tid} />
                        })}
                      </Box>
                    </TableCell>
                    <TableCell>{r.notes || '—'}</TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            if (confirm('Xóa phân công này?')) void deleteAssignment(r.id)
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  )
}
